import styles from "./FormSignDocument.module.css";
import { Formik, Form } from "formik";
import { Form8973Context } from "../../contexts";
import { useContext, useEffect, useRef, useState } from "react";
import { Footer } from "../Footer/Footer";
import { WORKFLOW_STEPS } from "../../constants";
import FormHeader from "../FormHeader/FormHeader";
import HelloSign from "hellosign-embedded";
import { VARIABLE_NAMES, getEnvVariable } from "lib/environment";
import { ErrorAlert } from "../ErrorAlert/ErrorAlert";
import { datadogRum } from "@datadog/browser-rum";

const { formStepContentContainer, formStepContent, reviewInfo, helloSign } =
  styles;

const FormSignDocument: React.FC = () => {
  const { formValues, embeddedSignUrl, setStep, companyValues } =
    useContext(Form8973Context);
  const helloSignFrameRef = useRef<HTMLDivElement>(null);
  const hasSignedRef = useRef(false);
  const clientId = getEnvVariable(VARIABLE_NAMES.DROPBOX_SIGN_CLIENT_ID);
  const isProduction = import.meta.env.NODE_ENV === "production";
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!embeddedSignUrl || !helloSignFrameRef.current) return;

    const client = new HelloSign();
    client.open(embeddedSignUrl, {
      clientId: clientId,
      container: helloSignFrameRef.current,
      allowCancel: false,
      skipDomainVerification: true,
      testMode: !isProduction,
    });

    client.on("open", () => {
      datadogRum.addAction("form8973_sign_iframe_opened", {
        companyUuid: companyValues.uuid,
      });
    });

    client.on("sign", () => {
      hasSignedRef.current = true;
      datadogRum.addAction("form8973_sign_completed", {
        companyUuid: companyValues.uuid,
      });
      setStep(WORKFLOW_STEPS.CONFIRMATION_SUCCESS);
    });

    client.on("error", (data) => {
      datadogRum.addError(
        new Error(`Form 8973: HelloSign error event (code: ${data.code})`),
        { companyUuid: companyValues.uuid, errorCode: data.code }
      );
      setError(
        "Something went wrong while loading the signing document. Please go back and try again. If this issue persists, contact support."
      );
    });

    client.on("close", () => {
      if (!hasSignedRef.current) {
        datadogRum.addError(
          new Error("Form 8973: HelloSign iframe closed without sign event"),
          { companyUuid: companyValues.uuid }
        );
        setError(
          "We couldn't confirm your signature was completed. Please go back and try again. If this issue persists, contact support."
        );
      }
    });

    return () => {
      client.close();
    };
  }, [embeddedSignUrl, setStep, clientId, isProduction, companyValues.uuid]);

  return (
    <Formik initialValues={formValues} onSubmit={() => {}}>
      {() => (
        <Form>
          <FormHeader />
          <div className={formStepContentContainer}>
            {error && <ErrorAlert error={error} />}
            <div className={formStepContent}>
              <h2 className={reviewInfo}>
                Next, double check the form is correct. Go Back to make any
                edits.
              </h2>
              <div className={helloSign} ref={helloSignFrameRef} />
            </div>
          </div>
          <Footer />
        </Form>
      )}
    </Formik>
  );
};

export default FormSignDocument;
